import React from 'react';
import styled, { keyframes } from 'styled-components';

const rotate = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const SynergySection = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  padding: 5rem 2rem;
  margin: 3rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '🤝';
    position: absolute;
    font-size: 15rem;
    opacity: 0.05;
    top: 50%;
    left: 50%;
    animation: ${rotate} 20s linear infinite;
  }

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const SynergyContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Title = styled.h2`
  text-align: center;
  color: #2e7d32;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const SynergyDiagram = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin: 3rem 0;
  flex-wrap: wrap;
  gap: 2rem;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const SynergyNode = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 50%;
  width: 200px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  font-weight: bold;
  color: #2e7d32;
  transition: transform 0.4s, box-shadow 0.4s;
  animation: ${popIn} 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  opacity: 0;
  position: relative;

  &:nth-child(1) { animation-delay: 0.2s; }
  &:nth-child(2) { animation-delay: 0.4s; }
  &:nth-child(3) { animation-delay: 0.6s; }

  &:hover {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 15px 40px rgba(46, 125, 50, 0.3);
  }

  &::after {
    content: '→';
    position: absolute;
    right: -3rem;
    font-size: 2rem;
    color: #4CAF50;
    opacity: 0.5;
  }

  &:last-child::after {
    content: '↺';
    right: auto;
    left: 50%;
    bottom: -3rem;
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    width: 210px;
    height: 210px;
    padding: 1.25rem;

    &::after {
      display: none;
    }
  }
`;

const NodeIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 1.7rem;
    margin-bottom: 0.35rem;
  }
`;

const NodeMain = styled.div`
  font-size: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const NodeArrow = styled.div`
  font-size: 0.9rem;
  margin-top: 0.5rem;
  line-height: 1;

  @media (max-width: 768px) {
    margin-top: 0.35rem;
    font-size: 0.85rem;
  }
`;

const NodeSub = styled.div`
  font-size: 0.85rem;
  line-height: 1.2;
  padding: 0 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.78rem;
  }
`;

const BenefitsCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  margin-top: 3rem;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
`;

const BenefitsTitle = styled.h3`
  color: #2e7d32;
  margin-bottom: 1rem;
  text-align: center;
`;

const Synergy: React.FC = () => {
  return (
    <SynergySection>
      <SynergyContent>
        <Title>The Power of Integration</Title>
        <Subtitle>
          Progress in one sector reinforces outcomes in others, creating virtuous cycles of development
        </Subtitle>

        <SynergyDiagram>
          <SynergyNode>
            <NodeIcon>💪</NodeIcon>
            <NodeMain>Better Health</NodeMain>
            <NodeArrow>↓</NodeArrow>
            <NodeSub>Higher School Attendance</NodeSub>
          </SynergyNode>

          <SynergyNode>
            <NodeIcon>🎓</NodeIcon>
            <NodeMain>Better Education</NodeMain>
            <NodeArrow>↓</NodeArrow>
            <NodeSub>Improved Farming Practices</NodeSub>
          </SynergyNode>

          <SynergyNode>
            <NodeIcon>🌱</NodeIcon>
            <NodeMain>Better Agriculture</NodeMain>
            <NodeArrow>↓</NodeArrow>
            <NodeSub>Enhanced Nutrition &amp; Income</NodeSub>
          </SynergyNode>
        </SynergyDiagram>

        <BenefitsCard>
          <BenefitsTitle>Cross-Sectoral Benefits</BenefitsTitle>
          <p>Integration creates multiplier effects: Healthy children learn better, educated farmers innovate, productive agriculture funds health programs.</p>
        </BenefitsCard>
      </SynergyContent>
    </SynergySection>
  );
};

export default Synergy;